import React from 'react';
import AppModal from './AppModal';
import { Form, Input, Select } from 'antd';
import { PlusOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import SidePanel from '@/components/ui/SidePanel';
import UserDetailPanel from './UserDetailPanel';
import { useUsersPage } from '@hooks/users/useUsersPage';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    RolesSliderTabWrap,
    RolesSearchWrapper,
    RolesSearchInput,
    RolesFilterBtn,
    CreateRoleModalBody,
    CreateRoleModalFooter,
} from './style';

const Users = () => {
    const {
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        filterRole,
        setFilterRole,
        filterStatus,
        setFilterStatus,
        filterModalOpen,
        setFilterModalOpen,
        activeFilterCount,
        clearFilters,
        roleFilterOptions,
        statusFilterOptions,
        inviteModalOpen,
        setInviteModalOpen,
        createModalOpen,
        setCreateModalOpen,
        panelOpen,
        closePanel,
        openPanel,
        selectedUserId,
        selectedUserName,
        total,
        activeCount,
        tableData,
        columns,
        users,
        inviteForm,
        createForm,
        editForm,
        handleInviteUser,
        handleCreateUser,
        handleEditUser,
        roles,
        canView,
        canInvite,
        canCreate,
        inviteUserMutation,
        createUserMutation,
        updateUserMutation,
        editModalOpen,
        setEditModalOpen,
        editingUser,
    } = useUsersPage();

    return (
        <ListBodyLayout
            title="Users"
            description="Manage users and invitations"
            actions={
                <Container $display="flex" $alignItems="center" $gap="12px">
                    {(canInvite || canView) && (
                        <Button
                            $primary={false}
                            $blackOnLight
                            onClick={() => setInviteModalOpen(true)}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Invite User
                        </Button>
                    )}
                    {(canCreate || canView) && (
                        <Button
                            $primary
                            $blackOnLight
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalOpen(true)}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Create User
                        </Button>
                    )}
                </Container>
            }
            kpiCards={[
                {
                    title: 'Total Users',
                    value: total,
                    change: { value: 'total', isPositive: true, text: 'in workspace' },
                },
                {
                    title: 'Active',
                    value: activeCount,
                    change: { value: '', isPositive: true, text: 'Active users' },
                },
                {
                    title: 'With Roles',
                    value: users.filter((u) => u.roles?.length).length,
                    change: { value: '', isPositive: true, text: 'Have role assigned' },
                },
                {
                    title: 'Plans',
                    value: [...new Set(users.map((u) => u.plan?.key).filter(Boolean))].length,
                    change: { value: '', isPositive: true, text: 'Plan types' },
                },
            ]}
        >
            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="16px"
            >
                <Container $display="flex" $alignItems="center" $minWidth="0">
                    <RolesSliderTabWrap>
                        <SliderTab
                            items={[
                                { key: STATUS_TAB.ALL, label: 'All' },
                                { key: STATUS_TAB.ACTIVE, label: 'Active' },
                                { key: STATUS_TAB.INACTIVE, label: 'Inactive' },
                            ]}
                            activeKey={statusTab}
                            onChange={setStatusTab}
                            fitContent
                        />
                    </RolesSliderTabWrap>
                </Container>
                <Container $display="flex" $alignItems="center" $gap="10px">
                    <RolesSearchWrapper>
                        <SearchOutlined />
                        <RolesSearchInput
                            placeholder="Search here"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </RolesSearchWrapper>
                    <RolesFilterBtn type="button" onClick={() => setFilterModalOpen(true)}>
                        <FilterOutlined />
                        Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                    </RolesFilterBtn>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                pageSize={10}
                $topMargin={false}
                $radius={12}
                onRow={(record) => ({
                    onClick: () => canView && openPanel(record.id),
                    style: { cursor: canView ? 'pointer' : 'default' },
                })}
            />

            <AppModal
                size="md"
                title="Invite User"
                open={inviteModalOpen}
                onCancel={() => {
                    setInviteModalOpen(false);
                    inviteForm.resetFields();
                }}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={inviteForm} layout="vertical">
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Enter email' },
                                { type: 'email', message: 'Enter a valid email' },
                            ]}
                        >
                            <Input placeholder="e.g. user@company.com" size="large" />
                        </Form.Item>
                        <Form.Item name="roleId" label="Role" rules={[{ required: true, message: 'Select a role' }]}>
                            <Select
                                placeholder="Select role"
                                size="large"
                                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        onClick={() => {
                            setInviteModalOpen(false);
                            inviteForm.resetFields();
                        }}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $padding="10px 16px"
                        $size="14px"
                        loading={inviteUserMutation.isPending}
                        onClick={handleInviteUser}
                    >
                        Send Invite
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Create User"
                open={createModalOpen}
                onCancel={() => {
                    setCreateModalOpen(false);
                    createForm.resetFields();
                }}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={createForm} layout="vertical">
                        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter name' }]}>
                            <Input placeholder="e.g. Jane Doe" size="large" />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[
                                { required: true, message: 'Enter email' },
                                { type: 'email', message: 'Enter a valid email' },
                            ]}
                        >
                            <Input placeholder="e.g. newuser@company.com" size="large" />
                        </Form.Item>
                        <Form.Item
                            name="password"
                            label="Password"
                            rules={[
                                { required: true, message: 'Enter password' },
                                { min: 8, message: 'Password must be at least 8 characters' },
                            ]}
                        >
                            <Input.Password placeholder="e.g. SecurePass123!" size="large" />
                        </Form.Item>
                        <Form.Item name="roleId" label="Role" rules={[{ required: true, message: 'Select a role' }]}>
                            <Select
                                placeholder="Select role"
                                size="large"
                                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        onClick={() => {
                            setCreateModalOpen(false);
                            createForm.resetFields();
                        }}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $padding="10px 16px"
                        $size="14px"
                        loading={createUserMutation.isPending}
                        onClick={handleCreateUser}
                    >
                        Create User
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title={editingUser?.name ? `Edit ${editingUser.name}` : 'Edit User'}
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false);
                    editForm.resetFields();
                }}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form form={editForm} layout="vertical">
                        <Form.Item name="name" label="Name">
                            <Input placeholder="Name" size="large" disabled />
                        </Form.Item>
                        <Form.Item name="email" label="Email">
                            <Input placeholder="Email" size="large" disabled />
                        </Form.Item>
                        <Form.Item
                            name="status"
                            label="Status"
                            rules={[{ required: true, message: 'Select status' }]}
                        >
                            <Select
                                placeholder="Select status"
                                size="large"
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'suspended', label: 'Suspended' },
                                    { value: 'deactivated', label: 'Deactivated' },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="roleId"
                            label="Role"
                            rules={[{ required: true, message: 'Select a role' }]}
                        >
                            <Select
                                placeholder="Select role"
                                size="large"
                                options={roles.map((r) => ({ value: r.id, label: r.name }))}
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        onClick={() => {
                            setEditModalOpen(false);
                            editForm.resetFields();
                        }}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $padding="10px 16px"
                        $size="14px"
                        loading={updateUserMutation.isPending}
                        onClick={handleEditUser}
                    >
                        Save changes
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <AppModal
                size="md"
                title="Filter Users"
                open={filterModalOpen}
                onCancel={() => setFilterModalOpen(false)}
                footer={null}
                styles={{ body: { paddingTop: 8 } }}
            >
                <CreateRoleModalBody>
                    <Form layout="vertical">
                        <Form.Item label="Role">
                            <Select
                                placeholder="All roles"
                                value={filterRole}
                                onChange={setFilterRole}
                                options={roleFilterOptions}
                                allowClear
                            />
                        </Form.Item>
                        <Form.Item label="Status">
                            <Select
                                placeholder="All statuses"
                                value={filterStatus}
                                onChange={setFilterStatus}
                                options={statusFilterOptions}
                                allowClear
                            />
                        </Form.Item>
                    </Form>
                </CreateRoleModalBody>
                <CreateRoleModalFooter>
                    <Button
                        $primary={false}
                        onClick={() => {
                            clearFilters();
                            setFilterModalOpen(false);
                        }}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Clear All
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        onClick={() => setFilterModalOpen(false)}
                        $padding="10px 16px"
                        $size="14px"
                    >
                        Apply
                    </Button>
                </CreateRoleModalFooter>
            </AppModal>

            <SidePanel open={panelOpen} onClose={closePanel} title={selectedUserName} width="480px">
                <UserDetailPanel userId={selectedUserId} />
            </SidePanel>
        </ListBodyLayout>
    );
};

export default Users;
